import type { OrderState } from './contracts';

/**
 * Clause 14.3 of the publishing agreement, as arithmetic.
 *
 * > Beyond that, GOODBUILD offers a self-serve refund on a copy with **under
 * > {{refund_playtime_hours}} hours played, within {{refund_days}} days** of
 * > purchase. Playtime is measured from real recorded sessions rather than from
 * > what a Buyer says.
 *
 * The clause is written with merge fields precisely so that the contract quotes
 * this file rather than restating it, the same arrangement every commercial
 * figure in that document already uses — see `lib/firma/merge-values.ts`, which
 * imports the two constants below and where the reasoning is written out at
 * length. A refund window typed into a contract is a number that was true when
 * somebody typed it; a refund window read from here is the number the code will
 * actually apply on the day a buyer presses the button.
 *
 * `/publishing` used to state the same two numbers publicly, in prose rather
 * than as a consumer of these constants — "under two hours played and inside
 * fourteen days is automatic". #142 removed that page, so these constants and
 * the contract template are the only places the window is written, and the test
 * that asserts the contract's rendered figures equal what is here is the whole
 * of the agreement between them.
 *
 * ## This is an offer on top of the law, never a replacement for it
 *
 * Clause 14.1 and 14.2 are the Buyer's actual rights: GOODBUILD is the merchant
 * of record, carries the consumer-law refund obligations, and cannot refuse a
 * lawful refund. **Nothing in this file limits any of that.** What it decides is
 * whether a refund happens *without a person being involved*, and every refusal
 * below therefore ends by pointing at a person rather than at a wall. A refusal
 * here means "not automatically", and it must never be read or worded as "no".
 *
 * That is also why the abuse guards further down are defensible at all: capping
 * a convenience is a commercial decision GOODBUILD is free to make, where
 * capping a statutory right would not be.
 *
 * Pure. No database, no clock, no network — it takes a description of an order
 * and returns a decision, which is what lets every branch below be tested
 * without a payment existing. `self-serve-refund.ts` is the half that reads D1.
 */

/** Clause 14.3. Under this many hours played. */
export const REFUND_PLAYTIME_HOURS = 2;

/** Clause 14.3. Within this many days of purchase. */
export const REFUND_DAYS = 14;

/**
 * The threshold as `play_session.active_minutes` actually stores it.
 *
 * Derived rather than written as 120, so the hours figure stays the single
 * place either number is decided.
 */
export const REFUND_PLAYTIME_MINUTES = REFUND_PLAYTIME_HOURS * 60;

/**
 * How many self-serve refunds one account gets on one title. Ever.
 *
 * **This is the guard against the obvious abuse**: refund, re-buy on the next
 * sale, refund again, indefinitely, at a cost to the studio of a processor fee
 * per cycle that clause 14.5 says GOODBUILD swallows. One per title makes the
 * loop terminate after one turn without touching the honest case at all, since
 * a buyer refunding the same game twice is not a case that exists honestly.
 *
 * Counted from `refund` rows against the buyer's own order lines for that
 * product, so it survives the re-buy: the new order is a different order and a
 * different entitlement, but it is the same account and the same title, which
 * is the pair the count is over. Nothing is stored to make this work and nothing
 * needs to be — hard rule 2, and the refund rows are already the record.
 */
export const SELF_SERVE_REFUNDS_PER_TITLE = 1;

/**
 * A ceiling across the whole account, and the window it is measured over.
 *
 * The per-title rule alone still leaves breadth: buy fifty games, play each for
 * ninety minutes, refund all fifty. Five in ninety days is a figure a real buyer
 * will essentially never reach — it is more refunds than most accounts have
 * purchases — and somebody who does reach it gets a person rather than a
 * refusal, which is the correct handling for a case that is either fraud or
 * something genuinely wrong.
 *
 * **It counts every refund on the account, not only self-serve ones**, and that
 * is deliberate rather than sloppy. The alternative is to count only refunds
 * this code initiated, which means discriminating on `refund.reason` — a free
 * text column anything may write — and a policy limit that hangs on a string
 * comparison is a policy limit that silently stops working the first time
 * somebody rewords a sentence. Counting all of them errs towards sending an
 * unusual account to a human, which is the safe direction: a chargeback and a
 * consumer-law refund are both facts worth a person seeing, and neither is
 * removed by this. What it costs is that a buyer with several *legitimate*
 * refunds loses the button early and has to ask. That is a fair trade for a
 * guard that cannot be worded around.
 */
export const SELF_SERVE_REFUNDS_PER_WINDOW = 5;
export const SELF_SERVE_REFUND_WINDOW_DAYS = 90;

/**
 * Why a self-serve refund is not available, in a form both halves of the
 * product can use.
 *
 * The shape is `PurchaseProblem`'s, deliberately, and for the reason stated there:
 * `code` is for a client deciding which repair to offer, and `message` is the
 * sentence the person actually reads. A bare boolean would make every caller
 * invent its own wording, and the wording is most of the work here — "you have
 * played it for three hours" and "you bought it five weeks ago" are the same
 * `false` and completely different things to be told.
 */
export type RefundRefusalCode =
  /** The payment has not settled, so there is nothing to reverse. */
  | 'not_cleared'
  /** This order has already been refunded, whole or in part. */
  | 'already_refunded'
  /** The copy is no longer held — a chargeback, a closed wave, an earlier revocation. */
  | 'no_longer_held'
  /** Past `REFUND_PLAYTIME_HOURS`, measured from recorded sessions. */
  | 'too_much_played'
  /** Past `REFUND_DAYS` from the purchase. */
  | 'too_late'
  /** `SELF_SERVE_REFUNDS_PER_TITLE` is spent on this title. */
  | 'title_already_refunded'
  /** `SELF_SERVE_REFUNDS_PER_WINDOW` is spent on this account. */
  | 'too_many_refunds'
  /** No provider payment recorded against the order, so nothing can be sent back. */
  | 'no_payment_on_file';

export interface RefundRefusal {
  code: RefundRefusalCode;
  /**
   * The title this is about, for a client that wants to point at it. Null when
   * the refusal is about the order as a whole rather than one copy in it.
   */
  productSlug: string | null;
  /** Written for a person to read and act on. Section 8: say what to do. */
  message: string;
}

/** One copy in an order, and everything the policy needs to know about it. */
export interface RefundCopyFacts {
  productId: string;
  productName: string;
  productSlug: string;
  /**
   * Summed `play_session.active_minutes` for this account and this title.
   *
   * **Activity, not wall clock, and that decision is not re-made here.**
   * `apps/launcher-core/src/session.rs` counts distinct minute buckets
   * containing input or progression events; `lib/play-sessions.ts` carries,
   * clamps and stores that number without recomputing it; this reads what was
   * stored. Section 5.4 is why: a player who launched a game and walked away
   * for six hours has not played it for six hours, and treating that as
   * playtime denies a refund to somebody entitled to one.
   *
   * **Zero when nothing was ever recorded, and that is eligible.** See
   * `sumPlaytimeMinutes` in `self-serve-refund.ts`, where the consequence is
   * written out in full.
   */
  activeMinutesPlayed: number;
  /** True when the entitlement this line granted has already been revoked. */
  entitlementRevoked: boolean;
  /**
   * How many refunds this account has already had on this title, **excluding
   * the order being asked about**. See `SELF_SERVE_REFUNDS_PER_TITLE`.
   */
  priorRefundsForTitle: number;
}

/** An order, and everything the policy needs to know about it. */
export interface RefundOrderFacts {
  state: OrderState;
  /** When the payment settled. Null until it has. Clause 14.3's clock starts here. */
  clearedAt: Date | null;
  /** `order.stripe_payment_intent_id`. What the provider is asked to reverse. */
  providerPaymentId: string | null;
  /** Minor units already refunded against this order, summed from `refund` rows. */
  alreadyRefundedMinor: number;
  /** Refunds of any kind on this account inside `SELF_SERVE_REFUND_WINDOW_DAYS`. */
  refundsInWindow: number;
  /** Every line on the order. See `decideSelfServeRefund` for why all of them. */
  copies: RefundCopyFacts[];
  /** Injectable so a test can pin the clock the window is measured against. */
  now: Date;
}

/**
 * Either the refund is available, or here is why it is not.
 *
 * A discriminated union rather than a boolean with an errors array, for the
 * reason `PurchaseReadiness` is one: reading the eligible branch without having
 * checked `eligible` does not typecheck, so a surface cannot draw the button
 * and then discover the refusal.
 */
export type SelfServeRefundDecision =
  | { eligible: true }
  | { eligible: false; refusals: RefundRefusal[] };

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whether clause 14.3's self-serve refund is available on this order.
 *
 * ## Why this is decided per order and not per copy
 *
 * The clause is written about "a copy", and eligibility genuinely is per copy —
 * playtime is per title. But the thing that reverses money is `refundOrder`,
 * which takes one `refundedPriceMinor` for a whole order and **allocates it
 * across the lines by price**. There is no way to express "reverse line two and
 * leave line one" through it, and rewriting that allocation to take a per-line
 * amount is a change to the one function in the codebase whose comment explains
 * where the loss of SERIALIZABLE actually bites.
 *
 * So the copies are each tested, and the order is refundable only when **every**
 * copy in it is. For every order that exists that is exactly the clause, because
 * a purchase is one title and writes one line per order — which is what the
 * Stripe webhook did and what the Creem one does. Were a provider ever to carry
 * several titles in one payment, this would be a strict subset of the clause: a
 * buyer who played one of three games is told which one, and asks a person,
 * rather than being silently given a third of their money back. That is the safe
 * direction to be wrong in, and it is the direction that needs no change to the
 * ledger to be correct.
 *
 * ## The order the checks run in is the order a person would want to hear them
 *
 * Structural facts first — unpaid, already refunded, no payment on file — then
 * the two the clause is actually about, then the guards. Every one of them is
 * evaluated, not short-circuited, because a buyer told "you played it too much"
 * who then discovers it was also too late has been answered twice for one
 * question.
 */
export function decideSelfServeRefund(facts: RefundOrderFacts): SelfServeRefundDecision {
  const refusals: RefundRefusal[] = [];

  /*
   * Named as a set rather than as `state !== 'cleared'`, because two of the six
   * states — `partially_refunded` and `refunded` — *are* settled orders and get
   * their own sentence below. Listing the settled ones means a seventh state
   * added later refuses by default rather than quietly becoming refundable,
   * which is the safe direction for a function that hands money back.
   */
  const settled =
    facts.state === 'cleared' || facts.state === 'partially_refunded' || facts.state === 'refunded';

  if (facts.clearedAt === null || !settled) {
    refusals.push({
      code: 'not_cleared',
      productSlug: null,
      message:
        'That order has not been paid for, so there is nothing to give back. ' +
        'If money did leave your account, ask us and a person will find it.',
    });
  }

  if (
    facts.alreadyRefundedMinor > 0 ||
    facts.state === 'refunded' ||
    facts.state === 'partially_refunded'
  ) {
    refusals.push({
      code: 'already_refunded',
      productSlug: null,
      message:
        'That order has already been refunded. It can take a few days to appear on your ' +
        'statement, depending on your bank.',
    });
  }

  if (facts.clearedAt !== null) {
    const days = Math.floor((facts.now.getTime() - facts.clearedAt.getTime()) / MS_PER_DAY);
    if (days >= REFUND_DAYS) {
      refusals.push({
        code: 'too_late',
        productSlug: null,
        message:
          `You bought that ${plural(days, 'day')} ago, and the refund button only covers the ` +
          `first ${REFUND_DAYS} days. Ask us and a person will look at it.`,
      });
    }
  }

  if (facts.refundsInWindow >= SELF_SERVE_REFUNDS_PER_WINDOW) {
    refusals.push({
      code: 'too_many_refunds',
      productSlug: null,
      message:
        `There have been ${plural(facts.refundsInWindow, 'refund')} on your account in the ` +
        `last ${SELF_SERVE_REFUND_WINDOW_DAYS} days, which is as many as the button allows. ` +
        'Ask us and a person will look at it.',
    });
  }

  /*
   * No payment id is a plumbing fault rather than a decision, and it is still a
   * refusal rather than a thrown error.
   *
   * Every order that cleared through a processor has one; an order without one
   * was written by a path that did not take money — a seed, a fixture, a hand
   * correction — and there is nothing for a provider to reverse. Throwing here
   * would put a 500 in front of a buyer for a fault that is entirely ours, and
   * a 500 tells them to try again forever. A sentence tells them to ask.
   */
  if (facts.clearedAt !== null && !facts.providerPaymentId) {
    refusals.push({
      code: 'no_payment_on_file',
      productSlug: null,
      message:
        'We cannot find the payment behind that order, so it cannot be refunded ' +
        'automatically. Ask us and a person will sort it out.',
    });
  }

  for (const copy of facts.copies) {
    if (copy.entitlementRevoked) {
      refusals.push({
        code: 'no_longer_held',
        productSlug: copy.productSlug,
        message:
          `${copy.productName} is no longer in your library, so there is nothing to give ` +
          'back. Ask us if that is a surprise.',
      });
    }

    if (copy.priorRefundsForTitle >= SELF_SERVE_REFUNDS_PER_TITLE) {
      refusals.push({
        code: 'title_already_refunded',
        productSlug: copy.productSlug,
        message:
          `You have already had a refund on ${copy.productName}, and the button only works ` +
          'once per game. Ask us and a person will look at it.',
      });
    }

    if (copy.activeMinutesPlayed >= REFUND_PLAYTIME_MINUTES) {
      refusals.push({
        code: 'too_much_played',
        productSlug: copy.productSlug,
        message:
          `You have played ${copy.productName} for ${formatPlaytime(copy.activeMinutesPlayed)}, ` +
          `and the refund button is for under ${REFUND_PLAYTIME_HOURS} hours played. We count ` +
          'real playing time, not how long the game was open. Ask us and a person will look ' +
          'at it.',
      });
    }
  }

  /*
   * An order with no lines cannot be refunded and is not a state anything
   * writes — `refundOrder` refuses it too. Named rather than falling through to
   * `eligible: true`, because falling through would hand the payment provider a
   * request for an order nobody can describe.
   */
  if (facts.copies.length === 0) {
    refusals.push({
      code: 'no_payment_on_file',
      productSlug: null,
      message: 'That order has nothing on it. Ask us and a person will look at it.',
    });
  }

  return refusals.length === 0 ? { eligible: true } : { eligible: false, refusals };
}

/**
 * The refusals as one paragraph, for a caller that has one line to say it in.
 *
 * Exactly `describePurchaseProblems`, for exactly its reason: these are already
 * written as sentences, a surface with room should render the list, and this is
 * for an error field or an HTTP body that carries a single `error` string the
 * way this API's do.
 */
export function describeRefundRefusals(refusals: readonly RefundRefusal[]): string {
  return refusals.map((refusal) => refusal.message).join(' ');
}

/**
 * Recorded playtime, in words a buyer would use.
 *
 * Whole minutes below an hour, hours and minutes above it, and never a decimal:
 * "1.75 hours" is a number nobody says out loud, and on the one screen where
 * somebody is checking our figure against their memory it invites arithmetic
 * rather than recognition.
 */
export function formatPlaytime(minutes: number): string {
  const whole = Math.max(0, Math.floor(minutes));
  if (whole < 60) return plural(whole, 'minute');

  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  return rest === 0 ? plural(hours, 'hour') : `${plural(hours, 'hour')} ${plural(rest, 'minute')}`;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
